import { createContext, useContext } from 'react';

export type AppShellLayout = 'full' | 'box';
export type NavbarMode = 'icon' | 'compact' | 'full';
export type SizingTier = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;

export interface AppShellContextValue {
  /** `'full'`: edge-to-edge layout · `'box'`: centered max-width content. */
  layout: AppShellLayout;
  /** Horizontal padding token applied to `<AppShell.Main>`. */
  paddingX: SizingTier;
  /** Vertical padding token. */
  paddingY: SizingTier;
  /** When `layout='box'`, the max-width of the content area. */
  maxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  /** Current Navbar render mode: drives label visibility on `<AppShell.NavLink>`. */
  navbarMode: NavbarMode;
  /** Setter so consumers can toggle the mode (e.g. via a Burger button). */
  setNavbarMode: (mode: NavbarMode) => void;
  /** Pixel widths the Navbar uses for each mode. */
  navbarWidth: { icon: number; compact: number; full: number };
  /** Header height (used for sticky offset on Main and stacking). */
  headerHeight: number;
  /** Footer height (used for layout). */
  footerHeight: number;
  /** Aside width. */
  asideWidth: number;
  /** Whether the navbar is currently mounted. */
  hasNavbar: boolean;
  /** Whether the aside is currently mounted. */
  hasAside: boolean;
}

export const AppShellContext = createContext<AppShellContextValue | null>(null);

export function useAppShell(): AppShellContextValue {
  const ctx = useContext(AppShellContext);
  if (!ctx) {
    // Sensible defaults if a sub-component is used outside the shell
    return {
      layout: 'full',
      paddingX: 'lg',
      paddingY: 'lg',
      maxWidth: 'xl',
      navbarMode: 'full',
      setNavbarMode: () => {},
      navbarWidth: { icon: 64, compact: 88, full: 280 },
      headerHeight: 60,
      footerHeight: 0,
      asideWidth: 320,
      hasNavbar: false,
      hasAside: false
    };
  }
  return ctx;
}
